import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Typography } from '@mui/material';
import { ChevronDown, ShieldAlert } from 'lucide-react';
import CopyableCode from '../shared/CopyableCode';
import InfoTip from '../shared/InfoTip';
import { CONNECT_APPS, PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import styles from './PlatformMcp.module.scss';

const KNOWN_KEYS = new Set(CONNECT_APPS.map((a) => a.key));

const toText = (value) => (typeof value === 'string' ? value : JSON.stringify(value, null, 2));

/** `claude_code` → "Claude code", for app keys the UI doesn't know yet. */
const humanizeKey = (key) => {
  const s = String(key).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const LinkApp = ({ app, value }) => {
  if (!value || typeof value !== 'object') {
    return <CopyableCode value={toText(value)} multiline ariaLabel={`Copy ${app.label} setup`} />;
  }
  return (
    <Box className={styles.appBody}>
      {value.steps && (
        <Typography variant="body2" className={styles.muted}>
          {value.steps}
        </Typography>
      )}
      {value.url && <CopyableCode label="LINK" value={value.url} ariaLabel="Copy link" />}
      {app.key === 'claude' && (
        <Typography variant="caption" className={styles.muted}>
          {PLATFORM_MCP_COPY.claudeTeamNote}
        </Typography>
      )}
    </Box>
  );
};

const CodeApp = ({ app, value }) => (
  <CopyableCode value={toText(value)} multiline ariaLabel={`Copy ${app.label} setup`} />
);

/**
 * Per-app connect instructions from a `connect` block — either the page's
 * (key shown as YOUR_QUADROID_CONNECTION_KEY) or a fresh create response.
 *
 * Link apps (Claude, ChatGPT, Town) carry the key inside the link; everything
 * else sends it in a header. Apps the backend adds later are listed after the
 * known ones and shown as text or JSON.
 */
const ConnectInstructions = ({ connect }) => {
  if (!connect) return null;
  const apps = connect.apps || {};

  const entries = [
    ...CONNECT_APPS.filter((a) => apps[a.key] !== undefined && apps[a.key] !== null),
    ...Object.keys(apps)
      .filter((k) => !KNOWN_KEYS.has(k) && apps[k] !== null && apps[k] !== undefined)
      .map((k) => ({ key: k, label: humanizeKey(k), kind: typeof apps[k] === 'string' ? 'code' : 'json' })),
  ];

  const header = connect.header;

  return (
    <Box className={styles.instructions}>
      {connect.warning && (
        <Alert severity="warning" icon={<ShieldAlert size={18} />}>
          {connect.warning}
        </Alert>
      )}

      {entries.map((app) => (
        <Accordion key={app.key} disableGutters className={styles.appAccordion}>
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box className={styles.appTitle}>
              {app.label}
              <InfoTip
                title={app.kind === 'link' ? PLATFORM_MCP_COPY.linkAppsTip : PLATFORM_MCP_COPY.headerAppsTip}
                size={14}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {app.kind === 'link' ? (
              <LinkApp app={app} value={apps[app.key]} />
            ) : (
              <CodeApp app={app} value={apps[app.key]} />
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {(connect.url || header) && (
        <Accordion disableGutters className={styles.appAccordion}>
          <AccordionSummary expandIcon={<ChevronDown size={18} />}>
            <Box className={styles.appTitle}>
              {PLATFORM_MCP_COPY.otherAppLabel}
              <InfoTip title={PLATFORM_MCP_COPY.headerAppsTip} size={14} />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={styles.appBody}>
              {connect.url && <CopyableCode label="SERVER LINK" value={connect.url} ariaLabel="Copy server link" />}
              {header?.name && (
                <>
                  <Typography variant="body2" className={styles.muted}>
                    {PLATFORM_MCP_COPY.otherAppHint}
                  </Typography>
                  <CopyableCode label="HEADER NAME" value={header.name} ariaLabel="Copy header name" />
                  <CopyableCode label="HEADER VALUE" value={header.value || ''} ariaLabel="Copy header value" />
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default ConnectInstructions;
